import { uniq } from "lodash"

import Firebase from "#classes/Firebase"
import { CLASS_GROUPS } from "#constants/classOptions"
import type { ClassConstructor } from "#customTypes/composedTypes/classTypes"
import { DOC_TYPES } from "#customTypes/composedTypes/firebaseTypes"
import {
  PORTFOLIO_CATEGORY,
  type PortfolioProjectStored,
  type PortfolioStageStored,
  type StudentPortfolioConstructor,
} from "#customTypes/composedTypes/portfolioTypes"
import type {
  StudentArtwork,
  StudentConstructor,
} from "#customTypes/composedTypes/studentTypes"
import type { Uuid } from "#customTypes/globalTypes"
import { cleanFormData } from "#utils/formHelpers"

import {
  createDefaultPortfolioProfile,
  viewModelToStudentPortfolioDoc,
} from "./portfolioConversions"

/**
 * Deterministic id for the per-student "Unassigned artwork" project.
 * Uses a literal space separator so it can't collide with generated ids,
 * and so the scanner app can compute it directly from `studentId`.
 */
export const getUnassignedProjectId = (studentId: Uuid): Uuid =>
  `unassignedArtwork ${studentId}` as Uuid

const createStageId = (studentId: Uuid, artworkId: Uuid): Uuid =>
  `unassignedStage ${studentId} ${artworkId}` as Uuid

const buildUnassignedProject = (
  studentId: Uuid,
  createdBy: Uuid,
  now: string
): PortfolioProjectStored => ({
  id: getUnassignedProjectId(studentId),
  title: "Unassigned artwork",
  category: PORTFOLIO_CATEGORY.UNASSIGNED,
  coverImageUrl: "",
  stages: {},
  createdAt: now,
  createdBy,
})

const buildStageForArtwork = (
  artwork: StudentArtwork,
  studentId: Uuid,
  createdBy: Uuid,
  now: string,
  sortIndex: number
): PortfolioStageStored => ({
  id: createStageId(studentId, artwork.id),
  label: artwork.filename || "Untitled",
  status: "in_progress",
  dueDate: "",
  artworkId: artwork.id,
  // New stage copies prefer the mobile crop when one exists. Existing stages
  // are never retro-synced when a crop is saved or reverted later.
  imageUrl: artwork.cropUrl ?? artwork.url,
  sortIndex,
  lastActivityAt: now,
  createdAt: now,
  createdBy,
})

/**
 * Ensures a `portfolios/{studentId}` document exists in Firestore, and:
 *   1. Seeds a deterministic "Unassigned artwork" project the scanner can write into.
 *   2. Backfills the unassigned project with one stage per portfolio-class artwork
 *      that isn't already referenced by any stage in any project.
 *   3. Flips `artwork.inPortfolio` to `true` on the student doc only when it was
 *      previously unset — explicit `false` is an opt-out and left alone.
 *
 * All writes run inside a single Firestore transaction so the student doc and
 * portfolio doc can never drift (e.g. `inPortfolio: true` on artwork with no
 * matching stage). If nothing actually changes, the transaction is a no-op.
 */
export const ensurePortfolioExists = async (
  studentId: Uuid,
  createdBy: Uuid
): Promise<void> => {
  const fb = Firebase.getInstance()

  // Pre-fetch the student's artwork classes outside the transaction so we can
  // look up `class.group` — Firestore transactions don't support queries.
  const student = await fb.getDocumentByRef<StudentConstructor>(
    fb.getRefById<StudentConstructor>(DOC_TYPES.STUDENTS, studentId)
  )
  const artworkList = Object.values(student?.artwork ?? {})
  const classIds = uniq(
    artworkList.map((a) => a.classId).filter((id): id is Uuid => !!id)
  )
  const classes =
    classIds.length > 0
      ? await fb.getDocumentsByIds<ClassConstructor>(
          DOC_TYPES.CLASSES,
          classIds
        )
      : []
  const classesById: Record<Uuid, ClassConstructor> = {}
  classes.forEach((c) => {
    classesById[c.id] = c
  })

  await fb.runTransaction(async (transaction) => {
    const portfolioRef = fb.getRefById<StudentPortfolioConstructor>(
      DOC_TYPES.PORTFOLIOS,
      studentId
    )
    const studentRef = fb.getRefById<StudentConstructor>(
      DOC_TYPES.STUDENTS,
      studentId
    )
    const [portfolioSnap, studentSnap] = await Promise.all([
      transaction.get(portfolioRef),
      transaction.get(studentRef),
    ])

    const existingPortfolio = portfolioSnap.exists()
      ? (portfolioSnap.data() as StudentPortfolioConstructor)
      : null
    const freshStudent = studentSnap.exists()
      ? (studentSnap.data() as StudentConstructor)
      : null

    const now = new Date().toISOString()
    const updateMeta = { lastUpdatedAt: now, lastUpdatedBy: createdBy }

    const baseDoc: StudentPortfolioConstructor =
      existingPortfolio ??
      viewModelToStudentPortfolioDoc(
        studentId,
        [],
        createDefaultPortfolioProfile(),
        [],
        [],
        null,
        {
          createdAt: now,
          createdBy,
          lastUpdatedAt: now,
          lastUpdatedBy: createdBy,
        }
      )

    let portfolioChanged = existingPortfolio == null

    const unassignedId = getUnassignedProjectId(studentId)
    const projects = { ...(baseDoc.projects ?? {}) }
    const projectOrder = [...(baseDoc.projectOrder ?? [])]

    if (!projects[unassignedId]) {
      projects[unassignedId] = buildUnassignedProject(studentId, createdBy, now)
      if (!projectOrder.includes(unassignedId)) {
        projectOrder.push(unassignedId)
      }
      portfolioChanged = true
    }

    const freshArtworkMap = freshStudent?.artwork ?? {}
    const freshArtworkList = Object.values(freshArtworkMap)
    const nextArtworkMap: Record<Uuid, StudentArtwork> = { ...freshArtworkMap }
    let studentChanged = false

    if (freshArtworkList.length > 0) {
      const linkedArtworkIds = new Set<string>()
      Object.values(projects).forEach((project) => {
        Object.values(project.stages ?? {}).forEach((stage) => {
          if (stage.artworkId) linkedArtworkIds.add(stage.artworkId)
        })
      })

      const unassignedProject = projects[unassignedId]
      const unassignedStages = { ...(unassignedProject.stages ?? {}) }
      const existingSortIndexes = Object.values(unassignedStages)
        .map((s) => s.sortIndex ?? 0)
        .filter((n) => Number.isFinite(n))
      let nextSortIndex = existingSortIndexes.reduce(
        (max, n) => (n > max ? n : max),
        -1
      )
      nextSortIndex += 1

      freshArtworkList.forEach((artwork) => {
        if (artwork.inPortfolio === false) return
        if (artwork.isHidden === true) return
        if (linkedArtworkIds.has(artwork.id)) return
        if (classesById[artwork.classId]?.group !== CLASS_GROUPS.PORTFOLIO) {
          return
        }

        const stage = buildStageForArtwork(
          artwork,
          studentId,
          createdBy,
          now,
          nextSortIndex
        )
        nextSortIndex += 1
        unassignedStages[stage.id] = stage
        linkedArtworkIds.add(artwork.id)
        portfolioChanged = true

        if (artwork.inPortfolio === undefined) {
          nextArtworkMap[artwork.id] = { ...artwork, inPortfolio: true }
          studentChanged = true
        }
      })

      if (portfolioChanged) {
        projects[unassignedId] = {
          ...unassignedProject,
          stages: unassignedStages,
        }
      }
    }

    if (portfolioChanged) {
      const nextDoc: StudentPortfolioConstructor = {
        ...baseDoc,
        projects,
        projectOrder: projectOrder as Uuid[],
        lastUpdatedAt: now,
        lastUpdatedBy: createdBy,
      }
      if (existingPortfolio) {
        // `cleanFormData` strips `undefined` fields (Firestore rejects them)
        // and trims text — matches what `fb.updateDocument` does.
        const cleaned = cleanFormData({ projects, projectOrder }) as Record<
          string,
          unknown
        >
        transaction.update(portfolioRef, { ...cleaned, ...updateMeta })
      } else {
        transaction.set(
          portfolioRef,
          cleanFormData(nextDoc) as StudentPortfolioConstructor
        )
      }
    }

    if (studentChanged) {
      const cleaned = cleanFormData({ artwork: nextArtworkMap }) as Record<
        string,
        unknown
      >
      transaction.update(studentRef, { ...cleaned, ...updateMeta })
    }
  })
}
